import { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import InfiniteScroll from "react-infinite-scroller";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  loadingMsg,
  noTransactions,
  noTransactionsSubMsg,
  recentTransactions,
  viewAll,
} from "./messages";
import { currencyImages, receiveIcon, sentIcon, timerIcon } from "./images";
import {
  callAPIInterface,
  dateTimeFormat,
  displayTaprootCurrency,
  filterDataFrom,
  getAmountFormatted,
  getCurrencyAmount,
  getDisplayCurrencySymbol,
  getTimeZoneAndDateFormat,
  getTimezoneMomentObj,
  noOfRecords,
  transactionTypeValues,
} from "./constants";
import Text from "./text/Text";
import Button from "./button/Button";
import ListItemSkeleton from "./ListItemSkeleton";
import ErrorMessageContent from "./ErrorMessageContent";
import FilterDrawer from "./FilterDrawer";
import { setTransactionFilterData } from "../redux/actions";
import SelectedFilters from "./SelectedFilters";
import { useSession } from "../hooks/useSession";
import StatementGenerateDrawer from "./StatementGenerateDrawer";
import moment from "moment-timezone";
import BackdropLoader from "./backdropLoader/BackdropLoader";
import StatementSentDrawer from "./StatementSentDrawer";

const openDetailPageArr = [
  "payment",
  "withdraw",
  "instant_send",
  "swap in",
  "swap out",
];

function TransactionCommon({
  isFromBrowser,
  customClass = "",
  isFromWallet = false,
  isCurrencyUpdated,
  open,
  setOpen,
  transactions,
  setTransactions,
  openStatementDrawer,
  setOpenStatementDrawer,
  openRequestSentDrawer,
  setOpenRequestSentDrawer,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { session } = useSession();

  const { taprootAsset, transactionFilterData, accountAssets, showBtcAmount } =
    useSelector((state) => state.common);

  const [hasMore, setHasMore] = useState(true);
  const [filterEndingBefore, setFilterEndingBefore] = useState(null);
  const userDateFormat = session?.user?.date_format || "DD/MM/YYYY";
  const [loading, setLoading] = useState(false);

  const { uuid: currency } = { ...taprootAsset };
  const fetchTransactionFilterData = async (
    lines,
    dataToFilter,
    isFromLoadMore = false
  ) => {
    try {
      let filterData = { ...dataToFilter };
      const currencies = accountAssets?.map((asset) => asset?.uuid);
      if (filterData) {
        filterData.limit = noOfRecords;
      }

      if (!filterData?.target_currency) {
        filterData.target_currency = {
          in: isFromWallet ? [currency] : currencies,
        };
      }

      if (isFromLoadMore && filterEndingBefore) {
        filterData.ending_before = filterEndingBefore;
      }

      const response = await callAPIInterface(
        "POST",
        "/balance-transactions/filter",
        filterData
      );
      if (!response?.has_more || !isFromBrowser) {
        setHasMore(false);
      } else {
        setFilterEndingBefore(response.data[response.data.length - 1].id);
      }
      setTransactions(lines.concat(response.data));
    } catch (err) {}
  };

  const handleRedirectionPath = (reqObj) => {
    const baseURL = location.pathname;
    let redirectionURL = "";
    if (reqObj?.target_currency) {
      redirectionURL += `&target_currency=${reqObj?.target_currency?.in}`;
    }
    if (reqObj?.type) {
      redirectionURL += `&source_type=${reqObj?.type?.in?.join(",")}`;
    }
    if (reqObj?.created?.gte) {
      redirectionURL += `&created_gte=${reqObj?.created?.gte}`;
    }
    if (reqObj?.created?.lte) {
      redirectionURL += `&created_lte=${reqObj?.created?.lte}`;
    }

    if (redirectionURL.length) {
      redirectionURL = "?" + redirectionURL.slice(1);
    }

    navigate(baseURL + redirectionURL);
  };

  useEffect(() => {
    if (isCurrencyUpdated) {
      const updatedCurrency = JSON.parse(
        localStorage.getItem("default_taproot_asset")
      )?.uuid;
    }
  }, [isCurrencyUpdated]);

  useEffect(() => {
    if (isFromBrowser || isFromWallet) {
      const search = location?.search;

      const targetCurrency = new URLSearchParams(search).get("target_currency");
      const sourceType = new URLSearchParams(search).get("source_type");
      const gteDateCreated = new URLSearchParams(search).get("created_gte");
      const lteDateCreated = new URLSearchParams(search).get("created_lte");

      let requestObj = {};
      if (targetCurrency) {
        requestObj.target_currency = {
          in: targetCurrency.split(","),
        };
      }
      if (sourceType) {
        requestObj.type = {
          in: sourceType.split(","),
        };
      }
      if (gteDateCreated) {
        requestObj.created = {
          ...requestObj.created,
          gte: gteDateCreated,
        };
      }
      if (lteDateCreated) {
        requestObj.created = {
          ...requestObj.created,
          lte: lteDateCreated,
        };
      }

      Object.keys(requestObj).length > 0 &&
        dispatch(setTransactionFilterData(requestObj));
    }
  }, [currency]);

  useEffect(() => {
    setTransactions([]);
    setFilterEndingBefore(null);
    setHasMore(true);
    if (Object.keys(transactionFilterData).length > 0 && !isFromBrowser) {
      handleRedirectionPath(transactionFilterData);
    }
    accountAssets.length > 0 &&
      fetchTransactionFilterData([], transactionFilterData);
  }, [transactionFilterData, accountAssets]);

  const loadMore = useCallback(() => {
    hasMore &&
      fetchTransactionFilterData(transactions, transactionFilterData, true);
  }, [transactions]);

  const handleOpenDetailPage = (transaction) => {
    const transactionData = {
      type: transaction?.type,
      id: transaction?.source,
      confirmed: true,
      transactionId: transaction.id,
      netAmount: transaction?.net,
    };

    localStorage.setItem("transactionDetail", JSON.stringify(transactionData));

    navigate(`/history/${transaction.id}`);
  };

  const transactionType = (type) => {
    return transactionTypeValues[type] ?? type;
  };

  const handleApplyFilter = (values) => {
    let requestData = {};
    const { assets, types, start_at, end_at } = values;

    if (assets.length > 0) {
      requestData.target_currency = {
        in: assets,
      };
    }
    if (types?.length > 0) {
      requestData.type = {
        in: types,
      };
    }
    if (start_at) {
      requestData.created = {
        ...requestData.created,
        gte: +getTimezoneMomentObj(start_at).startOf("day").format("x"),
      };
    }

    if (end_at) {
      requestData.created = {
        ...requestData.created,
        lte: +getTimezoneMomentObj(end_at).endOf("day").format("x"),
      };
    }

    handleRedirectionPath(requestData);
    dispatch(setTransactionFilterData(requestData));
  };

  const handleGenerateStatement = (values) => {
    const { timezone } = getTimeZoneAndDateFormat();
    const finalTimezone = timezone || moment.tz.guess();
    const {
      statement_period,
      start_at,
      end_at,
      currency,
      target_currency,
      status,
      metadata,
      sort_order,
      document_type,
      transaction_type,
      transaction_source,
      is_data_reconciliation_needed,
    } = values;

    const currencyCode = currency?.code;
    const targetCurrencyCode =
      target_currency !== "all" ? target_currency : null;
    const transactionType =
      transaction_type !== "all" ? transaction_type : null;

    let reportFromTimestamp = start_at;
    let reportToTimestamp = end_at;

    if (statement_period === "custom_date") {
      const endTimestamp = moment(end_at).tz(finalTimezone);
      const currentTimeStamp = moment().tz(finalTimezone);
      const isSlectedTodaysDate = endTimestamp.isSame(currentTimeStamp, "day");

      reportFromTimestamp = moment(start_at)
        .tz(finalTimezone)
        .startOf("day")
        .valueOf();

      reportToTimestamp = isSlectedTodaysDate
        ? currentTimeStamp.valueOf()
        : endTimestamp?.endOf("day")?.valueOf();
    }

    const apiPayload = {
      statement_type: "account",
      report_filters: {
        report_from_timestamp: reportFromTimestamp,
        report_to_timestamp: reportToTimestamp,
        ...(currency && { currency: [currencyCode] }),
        target_currency: targetCurrencyCode,
        transaction_type: transactionType,
        ...(transaction_source?.length && { transaction_source }),
        ...(metadata?.length && {
          metadata: metadata.map((metaObj) => metaObj.name),
        }),
        ...(status?.length && { status }),
      },
      document_type,
      sort_order,
      ...(is_data_reconciliation_needed !== undefined && {
        is_data_reconciliation_needed,
      }),
      timezone: finalTimezone,
    };

    setLoading(true);
    callAPIInterface("POST", `/report/generate`, apiPayload)
      .then((res) => {
        if (res) {
          handleStatementDrawerClose();
          setOpenRequestSentDrawer(true);
        }
      })
      .catch((error) => {
        console.log("Error while requesting statement:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleRemoveFilter = (key) => {
    let copyFilterData = { ...transactionFilterData };
    delete copyFilterData[key];
    delete copyFilterData.ending_before;

    if (
      Object.keys(copyFilterData).some((data) => filterDataFrom.includes(data))
    ) {
      dispatch(setTransactionFilterData(copyFilterData));
    } else {
      dispatch(setTransactionFilterData({}));
    }
    handleRedirectionPath(copyFilterData);
  };

  const handleClearAll = () => {
    dispatch(setTransactionFilterData({}));
    handleRedirectionPath({});
  };

  const handleFilterDrawerClose = () => {
    setOpen(false);
  };

  const handleStatementDrawerClose = () => {
    setOpenStatementDrawer(false);
  };

  const handleStatementSentDrawerClose = () => {
    setOpenRequestSentDrawer(false);
  };

  if (loading) {
    return (
      <BackdropLoader
        open={loading}
        alt="loading..."
        text={loadingMsg}
        customClass="loading-in-auth"
      />
    );
  }

  return (
    <>
      {isFromWallet && transactions?.length > 0 && (
        <Box className="recent-transactions">
          <Text className="default-text" size={20} font="semibold">
            {recentTransactions}
          </Text>
          <Button
            className="txt-uppercase"
            variant="outlined"
            iconPosition="start"
            icon="eyeIcon"
            label={viewAll}
            onClick={() => {
              dispatch(
                setTransactionFilterData({
                  target_currency: {
                    in: [currency],
                  },
                })
              );
              navigate(`/history?target_currency=${currency}`);
            }}
            sx={{ padding: "10px !important" }}
          />
        </Box>
      )}
      <Box
        className={`transaction-container ${customClass}`}
        sx={{ height: "100%" }}
      >
        {!isFromWallet && Object.entries(transactionFilterData).length > 0 && (
          <SelectedFilters
            handleRemoveFilter={handleRemoveFilter}
            handleClearAll={handleClearAll}
          />
        )}

        <InfiniteScroll
          pageStart={0}
          loadMore={loadMore}
          initialLoad={false}
          hasMore={hasMore}
          loader={<ListItemSkeleton />}
          className="transaction-list"
          useWindow={false}
        >
          {transactions.length > 0
            ? (isFromBrowser
                ? transactions
                : [...transactions].splice(0, 6)
              )?.map((transaction, index) => {
                const isOpenDetailPage = openDetailPageArr.includes(
                  transaction?.type?.toLowerCase()
                );

                const finalAmount = getCurrencyAmount(
                  transaction?.net,
                  transaction?.target_currency
                );
                return (
                  <Box
                    className="transaction"
                    key={transaction.id}
                    onClick={() => {
                      isOpenDetailPage && handleOpenDetailPage(transaction);
                    }}
                    sx={{
                      borderBottom:
                        transactions?.length - 1 === index
                          ? "none"
                          : "1px solid #f6f3f3",
                      ...(isOpenDetailPage && { cursor: "pointer" }),
                    }}
                  >
                    <Box className="left-side">
                      {transaction.transaction_type === "debit" ? (
                        <img src={sentIcon} height={30} width={30} />
                      ) : (
                        <img src={receiveIcon} height={30} width={30} />
                      )}
                      <Box ml="14px">
                        <Text className="grey-text" size={12} font="regular">
                          {dateTimeFormat(transaction?.created, userDateFormat)}
                        </Text>
                        <Text
                          className="default-text"
                          size={16}
                          font="semibold"
                        >
                          {transactionType(transaction?.type)}
                        </Text>
                      </Box>
                    </Box>
                    <Box className="right-side" textAlign="right">
                      <Box ml="14px">
                        <Text
                          className="grey-text"
                          size={16}
                          font="bold"
                          sx={{
                            color:
                              transaction.transaction_type === "debit"
                                ? "#FD371E !important"
                                : "#0CAD62 !important",
                          }}
                        >
                          {finalAmount < 0
                            ? "-"
                            : finalAmount > 0
                              ? transaction?.transaction_type === "debit"
                                ? "-"
                                : "+"
                              : ""}

                          {getDisplayCurrencySymbol(
                            transaction.target_currency,
                            showBtcAmount
                          )}

                          {getAmountFormatted(
                            Math.abs(finalAmount),
                            transaction.target_currency
                          )}
                        </Text>
                        <Text
                          className="default-text"
                          size={14}
                          font="semibold"
                        >
                          {displayTaprootCurrency(
                            finalAmount,
                            transaction.target_currency
                          )}
                        </Text>
                      </Box>
                    </Box>
                  </Box>
                );
              })
            : !hasMore && (
                <ErrorMessageContent
                  key="no-tx"
                  img={timerIcon}
                  msg={noTransactions}
                  subMsg={noTransactionsSubMsg}
                  padding={isFromWallet || isFromBrowser}
                />
              )}
        </InfiniteScroll>
      </Box>

      {open && (
        <FilterDrawer
          open={open}
          handleDrawerClose={handleFilterDrawerClose}
          handleApplyFilter={handleApplyFilter}
          isFromBrowser={isFromBrowser}
        />
      )}

      {openStatementDrawer && (
        <StatementGenerateDrawer
          open={openStatementDrawer}
          handleDrawerClose={handleStatementDrawerClose}
          handleGenerateStatement={handleGenerateStatement}
          isFromBrowser={isFromBrowser}
        />
      )}

      {openRequestSentDrawer && (
        <StatementSentDrawer
          open={openRequestSentDrawer}
          handleDrawerClose={handleStatementSentDrawerClose}
        />
      )}
    </>
  );
}

export default TransactionCommon;
